import React, { useState, useCallback, useEffect } from "react";
import { MasterDetailLayout, ConfirmDialog, Breadcrumb } from "../../../components/Common";
import AcquirerSidebar from "./components/AcquirerSidebar";
import AcquirerEditor from "./components/AcquirerEditor";
import { useAcquirers } from "./hooks/useAcquirers";
import { acquirerDefaults } from "../../../models/acquirer";

/**
 * Acquirers Page (Gestão de Adquirentes e Taxas)
 */
const AcquirersPage = () => {
    const {
        acquirers,
        bankAccounts,
        loading,
        isSaving,
        actions
    } = useAcquirers();

    const [selectedAcquirer, setSelectedAcquirer] = useState(acquirerDefaults);
    const [isDirty, setIsDirty] = useState(false);



    // Sincroniza seleção com a lista (atualização pós-save ou refresh)
    useEffect(() => {
        if (loading || acquirers.length === 0 || !selectedAcquirer?.id) return;

        // Se já temos uma seleção com ID, atualizar com dados mais recentes da lista
        const updated = acquirers.find(a => a.id === selectedAcquirer.id);
        if (updated) {
            // Só atualiza a referência se os dados mudaram
            if (updated !== selectedAcquirer) {
                setSelectedAcquirer(updated);
            }
        } else {
            // A adquirente selecionada foi deletada ou não encontrada, reseta
            setSelectedAcquirer(null);
        }
    }, [acquirers, loading, selectedAcquirer]);

    const handleSelectAcquirer = useCallback(async (acquirer) => {
        if (selectedAcquirer?.id === acquirer.id) return;

        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais nesta adquirente?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }

        setSelectedAcquirer(acquirer);
        setIsDirty(false);
    }, [selectedAcquirer, isDirty]);

    const handleCreateNew = useCallback(async () => {
        if (isDirty) {
            const confirmed = await ConfirmDialog({
                title: "Alterações não salvas",
                text: "Deseja descartar as mudanças atuais e cadastrar nova adquirente?",
                confirmButtonText: "Sim, descartar",
                cancelButtonText: "Cancelar"
            });
            if (!confirmed) return;
        }
        setSelectedAcquirer(acquirerDefaults);
        setIsDirty(false);
    }, [isDirty]);

    const handleSave = useCallback(async (data) => {
        const success = await actions.saveAcquirer(data);
        if (success) {
            setIsDirty(false);
        }
    }, [actions]);

    const handleDelete = useCallback(async (id) => {
        const acquirerToDelete = acquirers.find(a => a.id === id);

        const confirmed = await ConfirmDialog({
            title: "Excluir Adquirente?",
            text: `Tem certeza que deseja excluir "${acquirerToDelete?.name || 'esta adquirente'}"? Esta ação não pode ser desfeita.`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar"
        });
        if (!confirmed) return;

        const success = await actions.deleteAcquirer(id);
        if (success) {
            setSelectedAcquirer(null);
            setIsDirty(false);
        }
    }, [actions, acquirers]);

    const handleDirtyChange = useCallback((dirty) => setIsDirty(dirty), []);
    const handleCancel = useCallback(() => setIsDirty(false), []);

    // PageLoader logic removido para carregamento nativo do sidebar/detalhe

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Adquirentes e Taxas" />
            </div>
            
            <div className="border-top mb-3"></div>
            
            <MasterDetailLayout
                master={
                    <AcquirerSidebar
                        acquirers={acquirers}
                        activeId={selectedAcquirer?.id}
                        onSelect={handleSelectAcquirer}
                        onCreateNew={handleCreateNew}
                        loading={loading}
                    />
                }
                detail={
                    <AcquirerEditor
                        acquirer={selectedAcquirer}
                        bankAccounts={bankAccounts}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        onCancel={handleCancel}
                        isSaving={isSaving}
                        onChange={handleDirtyChange}
                    />
                }
            />
        </React.Fragment>
    );
};

export default AcquirersPage;
